import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import {
    setupPrimaryReceiverPeer,
    closePrimaryMemberPeer,
} from 'flow/api-clients/video-signaling-client';
import {
    selectVideoMembers,
    selectVideoWebSocket,
} from 'flow/selectors/video-selectors';
import { addMeetingLog } from 'flow/utils/meeting-utils';
import { WebsocketReadyStates } from 'flow/utils/meeting-constants';
import {
    createVideoSubscriptionManager,
    VideoSubscriptionManager,
} from 'flow/utils/video-subscription-manager';
import { isMemberReceiverPeerConsideredLive } from 'flow/utils/video-subscription-utils';

// A newly-visible tile subscribes only after a short hold, so a tile that merely
// flickers past during a layout reshuffle never opens a peer it immediately
// closes. Kept short so real appearances light up quickly.
const SUBSCRIBE_DELAY_MS = 300;
// A hidden tile lingers subscribed for a few seconds before teardown, so an
// active-speaker swap that briefly bumps someone out of the visible cap doesn't
// tear down and rebuild their peer. Deliberately longer than the subscribe gate.
const UNSUBSCRIBE_DELAY_MS = 3000;
// Hard floor between two actions on the same member, independent of the debounce
// gates — the last line of defence against sub/unsub churn when the visible set
// oscillates around one member.
const MIN_RESUBSCRIBE_MS = 2000;
// Cadence for flushing debounced actions that come due purely because time
// passed (no visibility change to drive them). The manager holds no timers of
// its own, so this tick is what makes a pending (un)subscribe eventually fire.
const TICK_MS = 500;
// How long a held subscription's peer must stay dead in the store before the
// manager treats it as externally torn down and re-subscribes (a signaling
// reconnect disposes every receiver peer while membership — and thus the
// visible set — never changes, so nothing else would re-fire). Well above a
// legitimate rebuild's close→create gap (milliseconds), so a restart never
// reads as a loss; a real loss stays dead forever, so the generous grace only
// delays recovery by a couple of seconds.
const PEER_LOST_GRACE_MS = 2000;

// Monotonic clock the manager reasons about (never wall-clock), so a system
// clock change can't skew the debounce/rate-limit windows.
const now = (): number => performance.now();

/**
 * Drives the visible-tile video subscriptions: given the set of member names
 * currently rendered on screen, subscribes the ones on screen and unsubscribes
 * the ones that leave — both debounced and rate-limited by
 * {@link createVideoSubscriptionManager}, so a churning grid doesn't thrash the
 * SFU with subscribe/unsubscribe renegotiations.
 *
 * A thin React shell around the pure manager: it owns the manager instance, the
 * flush timer and the Redux / meeting-log wiring, while all the policy lives in
 * the manager (and is unit-tested there in isolation). It also watches the
 * registered videoMembers set and forgets members that deregister
 * (participantLeft / teardown), so a member who leaves and quickly rejoins is
 * subscribed from scratch instead of "settling" against their closed peer, and
 * feeds the manager's peer-liveness self-healing from the store on every tick,
 * so peers torn down behind the manager's back (signaling reconnect) are
 * re-subscribed instead of staying black.
 * Audio is unaffected —
 * it travels over the separate SIP call, not these receiver peers — so an
 * off-screen speaker is still audible while their video is unsubscribed.
 *
 * Mount once for the whole meeting, from the single owner of the capped visible
 * set (MeetingVideoGrid).
 *
 * @param visibleMemberNames display names of the members actually rendered (the
 *   GRID_LIMIT-capped set, overflow excluded). Order is irrelevant.
 */
const useVideoSubscriptions = (visibleMemberNames: string[]): void => {
    const dispatch = useAppDispatch();
    const videoMembers = useAppSelector(selectVideoMembers);

    // Latest videoMembers for the tick interval below: the interval closure is
    // created once, so it reads the store through this ref instead of a stale
    // capture. Assigned every render (videoMembers' reference changes on any
    // member field write, which is exactly what liveness observation needs).
    const videoMembersRef = useRef(videoMembers);
    videoMembersRef.current = videoMembers;

    // The signaling socket, for the liveness gate in the tick below. Also read
    // through a ref — and readyState is read live off the object inside the
    // tick, because a socket dying does not by itself write to the store.
    const webSocket = useAppSelector(selectVideoWebSocket);
    const webSocketRef = useRef(webSocket);
    webSocketRef.current = webSocket;

    // Created once and kept for the hook's lifetime (mirrors the detector ref in
    // useOwnSpeakingDetection): passing the factory as useRef's argument would
    // rebuild it on every render even though only the first result is ever kept.
    // dispatch identity is stable, so the closures below stay valid for life.
    const managerRef = useRef<VideoSubscriptionManager | null>(null);
    if (managerRef.current === null) {
        managerRef.current = createVideoSubscriptionManager({
            onSubscribe: (name) => {
                dispatch(setupPrimaryReceiverPeer(name));
            },
            onUnsubscribe: (name) => {
                dispatch(closePrimaryMemberPeer(name));
            },
            subscribeDelayMs: SUBSCRIBE_DELAY_MS,
            unsubscribeDelayMs: UNSUBSCRIBE_DELAY_MS,
            minResubscribeMs: MIN_RESUBSCRIBE_MS,
            peerLostGraceMs: PEER_LOST_GRACE_MS,
            log: (message) =>
                dispatch(addMeetingLog(`useVideoSubscriptions: ${message}`)),
        });
    }

    // Forget members that deregistered from signaling (participantLeft removes
    // their videoMembers entry; meeting teardown clears the map). The leave path
    // already closed their peers, so the manager must drop its bookkeeping
    // WITHOUT emitting an unsubscribe — two failure modes otherwise:
    //  - Rejoin within the unsubscribe grace: the manager still holds
    //    subscribed=true for the name, so the returning member's tile "settles"
    //    against a peer that participantLeft closed — no re-subscribe ever
    //    fires and the tile is a dead avatar until the RestartVideoPlate.
    //  - No rejoin: the pending unsubscribe fires ~3s later against the
    //    departed member — a spurious cancelVideoFrom, and the peer-slot reset
    //    setIn-resurrects a ghost videoMembers entry for them.
    // Keyed on the VALUE of the registered-name set (same rationale as
    // visibleKey below): videoMembers' reference changes on every member field
    // write, but the effect only needs to run when membership changes.
    const registeredKey = JSON.stringify(
        (videoMembers?.keySeq().toArray() ?? []).sort((a, b) =>
            a.localeCompare(b),
        ),
    );
    // null until the first run so an initially-empty set isn't diffed against
    // itself; from then on, the previous registered names to diff departures.
    const previousRegisteredRef = useRef<string[] | null>(null);

    useEffect(() => {
        const manager = managerRef.current;
        if (!manager) {
            return;
        }
        const names = JSON.parse(registeredKey) as string[];
        const previous = previousRegisteredRef.current;
        previousRegisteredRef.current = names;
        if (previous === null) {
            return;
        }
        const departed = previous.filter((name) => !names.includes(name));
        if (departed.length === 0) {
            return;
        }
        departed.forEach((name) => manager.forgetMember(name));
        dispatch(
            addMeetingLog(
                `useVideoSubscriptions: forgot departed member(s) [${departed.join(
                    ', ',
                )}] — peers were torn down by the leave path; a rejoin re-subscribes from scratch`,
            ),
        );
    }, [registeredKey, dispatch]);

    // The previous visible set, so we can report which tiles entered/left on a
    // change (the subscribe/unsubscribe lines themselves come from the manager's
    // log sink once their debounce fires).
    const previousVisibleRef = useRef<string[]>([]);

    // Gate on the *value* of the visible set, not the array identity:
    // visibleMemberNames is rebuilt every render (and swapped every members
    // poll), so keying the effect on the array would refeed the manager on every
    // render/poll. Stringifying the sorted names makes an unchanged poll a true
    // no-op while real membership changes still fire. Cheap (bounded by
    // GRID_LIMIT); no useMemo — the array is a fresh ref each render anyway.
    const visibleKey = JSON.stringify(
        [...visibleMemberNames].sort((a, b) => a.localeCompare(b)),
    );

    useEffect(() => {
        const manager = managerRef.current;
        if (!manager) {
            return;
        }
        const names = JSON.parse(visibleKey) as string[];
        const previous = previousVisibleRef.current;
        const entered = names.filter((name) => !previous.includes(name));
        const left = previous.filter((name) => !names.includes(name));
        dispatch(
            addMeetingLog(
                `useVideoSubscriptions: visible tiles = [${names.join(
                    ', ',
                )}] | entered: [${entered.join(', ')}] | left: [${left.join(
                    ', ',
                )}]`,
            ),
        );
        previousVisibleRef.current = names;
        manager.setVisibleMembers(names, now());
    }, [visibleKey, dispatch]);

    // Flush debounced actions that come due purely because time passed — the
    // manager schedules nothing itself, so without this a member hidden with no
    // further visibility change would never reach its unsubscribe. The same
    // cadence feeds the manager's self-healing: report each held subscription's
    // actual peer liveness from the store, so a subscription whose peer was
    // torn down externally (a signaling reconnect disposes every receiver peer
    // while the members stay registered and the visible set never changes) is
    // dropped after PEER_LOST_GRACE_MS and re-subscribed by the normal
    // reconcile instead of staying a black tile forever.
    useEffect(() => {
        const manager = managerRef.current;
        if (!manager) {
            return undefined;
        }
        const interval = setInterval(() => {
            const nowMs = now();
            // Feed liveness only while the signaling socket is open. During a
            // reconnect the SIP call is re-established BEFORE the new socket
            // exists (meeting-supervisor#reconnect) — potentially seconds. A
            // demotion firing in that window would re-subscribe into a dead
            // socket: setupPrimaryReceiverPeer's setupVideoStreamFromMember
            // send is silently dropped, yet the locally-created peer lands in
            // the slot, reads as live and disarms the self-heal for good.
            // Pausing the feed makes the grace effectively count from
            // socket-open, by which point joinRoom (dispatched right after the
            // socket is up) has completed.
            const webSocketReady =
                webSocketRef.current?.readyState === WebsocketReadyStates.OPEN;
            if (webSocketReady) {
                manager.getSubscribedMembers().forEach((name) => {
                    manager.reportPeerLiveness(
                        name,
                        isMemberReceiverPeerConsideredLive(
                            videoMembersRef.current,
                            name,
                        ),
                        nowMs,
                    );
                });
            }
            manager.tick(nowMs);
        }, TICK_MS);
        return () => clearInterval(interval);
    }, []);

    // Drop all manager state on unmount (leaving the meeting tears the peers down
    // via the normal participantLeft / leave path).
    useEffect(
        () => () => {
            managerRef.current?.reset();
        },
        [],
    );
};

export default useVideoSubscriptions;
